import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Stack,
  Grid,
  Divider,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import {
  downloadSupplyPlanScheduledTemplate,
  uploadSupplyPlanScheduled,
  deleteSupplyPlanScheduled,
} from "api/resources/supplyPlan";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectMe, selectSupplyPlanScheduled } from "store/selectors";
import { fetchSupplyPlanScheduled } from "store/appSlice";
import { downloadXLSX } from "helpers/excelExport";
import {
  hasNPAdminSupplyPlanUploadWindowClosed,
  hasNPAuctionDeadlinePassed,
} from "helpers/auctions";
import FileUpload from "components/FileUpload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SupplyPlanData from "./SupplyPlanData";
import { grey } from "@mui/material/colors";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

interface ScheduledSupplyPlanProps {
  currentRoleClientId: number;
  selectedAuction: number;
  date: Dayjs;
  setSnackbar: (snackbar: { type: string; message: string }) => void;
}

const ScheduledSupplyPlan = ({
  currentRoleClientId,
  selectedAuction,
  date,
  setSnackbar,
}: ScheduledSupplyPlanProps) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const dispatch = useAppDispatch();
  const supplyPlanScheduled = useAppSelector(selectSupplyPlanScheduled);
  const me = useAppSelector(selectMe);

  const isAdmin =
    me?.roles?.some((role: { name: string }) => role.name === "ROLE_ADMIN") ??
    false;
  const deadlinePassed = hasNPAuctionDeadlinePassed(selectedAuction, date);
  const adminUploadWindowClosed = hasNPAdminSupplyPlanUploadWindowClosed(
    selectedAuction,
    date,
  );
  const canUpload = isAdmin ? !adminUploadWindowClosed : !deadlinePassed;
  const canRemove = !deadlinePassed || isAdmin;
  const showAdminLateUpload =
    isAdmin && deadlinePassed && !adminUploadWindowClosed;

  useEffect(() => {
    if (currentRoleClientId) {
      dispatch(
        fetchSupplyPlanScheduled(
          currentRoleClientId,
          selectedAuction,
          dayjs(date).format("YYYY-MM-DD"),
        ),
      );
    }
  }, [currentRoleClientId, selectedAuction, dispatch, date]);

  const handleUpload = () => {
    if (!canUpload) {
      setSnackbar({
        type: "error",
        message:
          "Upload window has closed. Scheduled supply plan can no longer be uploaded.",
      });
      return;
    }
    if (uploadedFile) {
      setUploading(true);

      uploadSupplyPlanScheduled(
        currentRoleClientId,
        selectedAuction,
        dayjs(date).format("YYYY-MM-DD"),
        uploadedFile,
      )
        .then(() => {
          setUploading(false);
          setUploadedFile(null);
          dispatch(
            fetchSupplyPlanScheduled(
              currentRoleClientId,
              selectedAuction,
              dayjs(date).format("YYYY-MM-DD"),
            ),
          );
          setSnackbar({
            type: "success",
            message: "Forecast uploaded",
          });
        })
        .catch((error) => {
          setUploading(false);
          setSnackbar({
            type: "error",
            message: error.response?.data?.message || error.message,
          });
          console.log({ error });
        });
    }
  };

  const handleDownloadScheduledSupplyPlanTemplate = () => {
    downloadSupplyPlanScheduledTemplate(dayjs(date).format("YYYY-MM-DD")).then(
      (result) => {
        downloadXLSX(result, `Scheduled_supply_plan_template.xlsx`);
      },
    );
  };

  const handleRemoveScheduledSupplyPlan = () => {
    if (!canRemove) {
      setSnackbar({
        type: "error",
        message:
          "Auction deadline has passed. Scheduled supply plan can no longer be removed.",
      });
      return;
    }
    deleteSupplyPlanScheduled(
      currentRoleClientId,
      selectedAuction,
      dayjs(date).format("YYYY-MM-DD"),
    )
      .then(() => {
        dispatch(
          fetchSupplyPlanScheduled(
            currentRoleClientId,
            selectedAuction,
            dayjs(date).format("YYYY-MM-DD"),
          ),
        );
        setSnackbar({
          type: "info",
          message: "Scheduled supply plan removed",
        });
      })
      .catch((error) => {
        console.error(error);
        setSnackbar({
          type: "error",
          message: "Error removing scheduled supply plan",
        });
      });
  };

  return (
    <Box>
      <Grid container columnSpacing={8} sx={{ mt: "1rem" }}>
        <Grid item sm={6}>
          {canUpload ? (
            <Stack spacing={2}>
              {showAdminLateUpload && (
                <Alert severity="warning">
                  Auction deadline has passed. After uploading, you must
                  manually regenerate the supply plans. Uploads are accepted up
                  to 40 minutes after the auction deadline.
                </Alert>
              )}
              <FileUpload
                uploadedFile={uploadedFile}
                setUploadedFile={setUploadedFile}
                handleUpload={handleUpload}
                loading={uploading}
              />
            </Stack>
          ) : (
            <Alert severity="error">
              Upload window has closed. Scheduled supply plan can no longer be
              uploaded.
            </Alert>
          )}
        </Grid>
        <Grid item sm={6}>
          <Typography component="p">
            {`Please upload your scheduled supply plan for the selected date
          (${dayjs(date).format("DD.MM.YYYY")}) in megawatts (MW)`}{" "}
            based on <strong>Europe/Oslo</strong> timezone.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleDownloadScheduledSupplyPlanTemplate()}
            sx={{ mt: 2 }}
          >
            Download template
          </Button>
        </Grid>
      </Grid>
      <Divider sx={{ margin: "2rem 0" }} />
      {!supplyPlanScheduled ? (
        <Stack alignItems="center" direction="row" gap={1}>
          <InfoIcon color="primary" />
          <Typography component="p">
            No scheduled supply plan uploaded
          </Typography>
        </Stack>
      ) : (
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Stack alignItems="center" direction="row" gap={1}>
            <CheckCircleIcon color="success" />
            <Typography component="p">
              Scheduled supply plan uploaded
            </Typography>
          </Stack>
          {canRemove && (
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() => handleRemoveScheduledSupplyPlan()}
              sx={{ mt: 2 }}
            >
              Remove supply plan
            </Button>
          )}
        </Box>
      )}
      {supplyPlanScheduled && (
        <Grid container sx={{ mt: "1rem" }}>
          <Grid item sm={12}>
            <Accordion>
              <AccordionSummary
                sx={{ backgroundColor: grey[200] }}
                expandIcon={<ExpandMoreIcon />}
              >
                <Typography component="span">Scheduled supply plan</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <SupplyPlanData data={supplyPlanScheduled} />
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ScheduledSupplyPlan;
